<script setup>
import { computed } from "vue";
import { mdiTagOutline } from "@mdi/js";
import Icon from "@/components/Icon.vue";
import { usePromo } from "@/globals/promo";

/* Same answer as the strip under the navbar — both read globals/promo.js, which
   asks the backend — so the two can't disagree about whether a promo is
   running, and neither can disagree with checkout. */
const { promo, isActive, label } = usePromo();

/* "Until 30 Sep" when the promo has an end date; nothing when it's open-ended,
   rather than inventing urgency the promo doesn't have. Formatted in UTC because
   the date is a UTC calendar day, not an instant. */
const endsLabel = computed(() => {
  if (!promo.endsOn) return null;
  const date = new Date(`${promo.endsOn}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
});
</script>

<template>
  <!-- Checkout discount promo strip -->
  <div v-if="isActive" class="mb-6 rounded-xl bg-gray-900 px-4 py-3">
    <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div class="flex items-start gap-3 min-w-0">
        <span
          class="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-gray-800 text-brand-400 flex-shrink-0"
          aria-hidden="true"
        >
          <icon :path="mdiTagOutline" :size="20" w="w-5" h="h-5" />
        </span>
        <div class="min-w-0">
          <p class="text-sm font-semibold text-white">
            {{ label ? `${label} every purchase` : "Discount on every purchase" }}
            — running now
          </p>
          <!-- Crypto is named only when it is true. The crypto service takes a
               percentage off its invoice and nothing else — that invoice is in
               USD, so a fixed euro amount has no clean equivalent there, and a
               promo reported without a number gives it nothing to apply.
               PayPal is not named at all: checkout offers whatever methods
               Stripe has enabled, and nothing in the panel can confirm PayPal
               is one of them. -->
          <p class="mt-0.5 text-sm text-gray-400">
            <template v-if="promo.percentOff">
              Applied automatically on the payment page, no code needed — card
              and crypto.
            </template>
            <template v-else>
              Applied automatically on the payment page when you pay by card, no
              code needed.
            </template>
          </p>
        </div>
      </div>

      <!-- A chip, not a button: there is nothing to do to claim this, so a
           call to action here would only suggest there was. -->
      <span
        class="inline-flex items-center flex-shrink-0 self-start sm:self-center rounded-full bg-brand-600 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white"
      >
        {{ endsLabel ? `Until ${endsLabel}` : "Auto-applied" }}
      </span>
    </div>
  </div>
</template>
